import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LineMask } from "../components/marketing/Reveal";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
      const dest = location.state?.from || (data.role === "admin" ? "/admin" : data.role === "planner" ? "/planner" : "/portal");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white grid lg:grid-cols-2 noise-overlay">
      <div className="flex flex-col justify-center px-6 sm:px-16 lg:px-24 py-16">
        <Link to="/" className="flex items-center gap-2 mb-14" data-testid="login-logo">
          <span className="h-8 w-8 rounded-lg bg-neon flex items-center justify-center font-display font-black text-ink text-sm">OX</span>
          <span className="font-display font-bold text-lg tracking-tight">OrthoPlani<span className="text-neon">X</span></span>
        </Link>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.0]">
          <LineMask delay={0.05}>Welcome back,</LineMask>
          <LineMask delay={0.2} className="text-neon">Doctor.</LineMask>
        </h1>
        <p className="mt-5 text-slate-400 text-sm max-w-md">Sign in to submit cases, track planning progress and download final treatment plans.</p>

        <form onSubmit={submit} className="mt-10 space-y-5 max-w-md" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="l-email" className="text-slate-300">Email</Label>
            <Input id="l-email" data-testid="login-email-input" type="email" required autoComplete="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="you@clinic.com" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="l-pass" className="text-slate-300">Password</Label>
              <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-neon font-semibold" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <Input id="l-pass" data-testid="login-password-input" type="password" required autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full rounded-full bg-neon text-ink font-bold hover:bg-neon/90 h-12 text-sm transition-transform hover:scale-[1.01]">
            {loading ? "Signing in…" : "Sign in"} <ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
        </form>

        <div className="mt-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="mt-6">
          <GoogleSignInButton label="Sign in with Google" />
        </div>

        <p className="mt-8 text-sm text-slate-500">
          New to OrthoPlaniX?{" "}
          <Link to="/register" className="text-neon font-semibold hover:underline" data-testid="login-register-link">Create a professional account</Link>
        </p>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBsb29raW5nJTIwYXQlMjB0YWJsZXR8ZW58MHx8fHwxNzg3NDEzNDE1fDA&ixlib=rb-4.1.0&q=85"
          alt="Doctors reviewing a digital treatment plan" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-display font-black text-3xl xl:text-4xl leading-tight">You Treat.<br /><span className="text-neon">We Plan.</span></p>
        </div>
      </div>
    </div>
  );
}
